import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const API = process.env.REACT_APP_SERVER_URL;

const PLATFORM_LABEL = { medium: "Medium", hashnode: "Hashnode", both: "Both" };
const STATUS_COLOR = {
  draft: "default",
  edited: "info",
  published: "success",
  pending: "warning",
  used: "success",
  archived: "default",
};

const emptySuggestion = {
  platform: "both",
  idea: "",
  notes: "",
  use_on: "",
  priority: 0,
};

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function BlogStudioPage() {
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [platformFilter, setPlatformFilter] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(emptySuggestion);
  const [saving, setSaving] = useState(false);

  const fetchDrafts = useCallback(async () => {
    const query = platformFilter ? `?platform=${platformFilter}` : "";
    const res = await fetch(`${API}/admin/blog-studio/drafts${query}`, {
      credentials: "include",
    });
    const data = await res.json();
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Unable to load blog drafts");
    }
    setDrafts(Array.isArray(data.data) ? data.data : []);
  }, [platformFilter]);

  const fetchSuggestions = useCallback(async () => {
    const res = await fetch(`${API}/admin/blog-studio/suggestions`, {
      credentials: "include",
    });
    const data = await res.json();
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Unable to load suggestions");
    }
    setSuggestions(Array.isArray(data.data) ? data.data : []);
  }, []);

  const refresh = useCallback(
    async ({ quiet } = {}) => {
      if (!quiet) setLoading(true);
      try {
        await Promise.all([fetchDrafts(), fetchSuggestions()]);
      } catch (error) {
        if (!quiet) toast.error(error.message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [fetchDrafts, fetchSuggestions],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A run takes a couple of minutes and finishes server-side, so poll
  // quietly until both of today's articles land, then stop.
  useEffect(() => {
    if (!generating) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(async () => {
      await refresh({ quiet: true });
      if (Date.now() - startedAt > 6 * 60 * 1000) setGenerating(false);
    }, 15000);
    return () => clearInterval(timer);
  }, [generating, refresh]);

  const todayDraftCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return drafts.filter((d) => d.run_date === today).length;
  }, [drafts]);

  useEffect(() => {
    if (generating && todayDraftCount >= 2) {
      setGenerating(false);
      toast.success("Both articles are ready — check your inbox.");
    }
  }, [generating, todayDraftCount]);

  const pendingCount = suggestions.filter(
    (s) => s.status === "pending",
  ).length;

  const handleGenerate = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    try {
      const res = await fetch(`${API}/admin/blog-studio/generate`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Could not start generation");
      }
      toast.info(data.message);
    } catch (error) {
      setGenerating(false);
      toast.error(error.message);
    }
  };

  const handleResend = async (draft) => {
    try {
      const res = await fetch(
        `${API}/admin/blog-studio/drafts/${draft.id}/resend`,
        { method: "POST", credentials: "include" },
      );
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Send failed");
      toast.success("Email sent");
      refresh({ quiet: true });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteDraft = async (draft) => {
    if (!window.confirm(`Delete the ${PLATFORM_LABEL[draft.platform]} draft "${draft.title || "untitled"}"?`)) {
      return;
    }
    try {
      const res = await fetch(`${API}/admin/blog-studio/drafts/${draft.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success("Draft deleted");
      refresh({ quiet: true });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openSuggestionDialog = (suggestion) => {
    setEditing(
      suggestion
        ? {
            id: suggestion.id,
            platform: suggestion.platform,
            idea: suggestion.idea || "",
            notes: suggestion.notes || "",
            use_on: suggestion.use_on || "",
            priority: suggestion.priority || 0,
          }
        : emptySuggestion,
    );
    setDialogOpen(true);
  };

  const handleSaveSuggestion = async () => {
    if (!editing.idea.trim()) {
      toast.error("Write the idea first");
      return;
    }
    setSaving(true);
    try {
      const isEdit = Boolean(editing.id);
      const res = await fetch(
        `${API}/admin/blog-studio/suggestions${isEdit ? `/${editing.id}` : ""}`,
        {
          method: isEdit ? "PUT" : "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            platform: editing.platform,
            idea: editing.idea,
            notes: editing.notes || null,
            use_on: editing.use_on || null,
            priority: editing.priority,
          }),
        },
      );
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Save failed");
      toast.success(isEdit ? "Suggestion updated" : "Suggestion saved");
      setDialogOpen(false);
      refresh({ quiet: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSuggestion = async (suggestion) => {
    if (!window.confirm("Delete this suggestion?")) return;
    try {
      const res = await fetch(
        `${API}/admin/blog-studio/suggestions/${suggestion.id}`,
        { method: "DELETE", credentials: "include" },
      );
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      refresh({ quiet: true });
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Blog Studio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One Medium article and one Hashnode article per run, written in your
            voice and emailed to you with the image prompts placed inline.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refresh()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={
              generating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
            onClick={() => setConfirmOpen(true)}
            disabled={generating}
          >
            {generating ? "Writing…" : "Generate now"}
          </Button>
        </Stack>
      </Stack>

      {generating && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Writing both articles. This takes a couple of minutes — the drafts
          appear here on their own and the email goes out when it's done.
        </Alert>
      )}

      {pendingCount > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {pendingCount} suggestion{pendingCount === 1 ? "" : "s"} waiting.
          Whatever is pending gets used on the next run.
        </Alert>
      )}

      <Paper variant="outlined">
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}
        >
          <Tab label={`Drafts (${drafts.length})`} />
          <Tab label={`Suggestions (${pendingCount} pending)`} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Stack direction="row" spacing={2} sx={{ p: 2 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  label="Platform"
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                >
                  <MenuItem value="">All platforms</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hashnode">Hashnode</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {loading ? (
              <Box sx={{ p: 5, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : drafts.length === 0 ? (
              <Box sx={{ p: 5, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No drafts yet. Hit “Generate now”, or wait for the next
                  scheduled run.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Words</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {drafts.map((draft) => (
                      <TableRow key={draft.id} hover>
                        <TableCell>{formatDate(draft.run_date)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={PLATFORM_LABEL[draft.platform]}
                            color={
                              draft.platform === "hashnode"
                                ? "primary"
                                : "default"
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          {draft.title ? (
                            <Typography variant="body2" fontWeight={600}>
                              {draft.title}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="error">
                              Generation failed
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {draft.topic || draft.error_message || ""}
                          </Typography>
                        </TableCell>
                        <TableCell>{draft.word_count || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={draft.status}
                            color={STATUS_COLOR[draft.status] || "default"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Open">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!draft.title}
                                onClick={() =>
                                  navigate(`/blog-studio/${draft.id}`)
                                }
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Email this to me again">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!draft.title}
                                onClick={() => handleResend(draft)}
                              >
                                <SendOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {draft.published_url && (
                            <Tooltip title="View published post">
                              <IconButton
                                size="small"
                                component="a"
                                href={draft.published_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDraft(draft)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Drop an idea here and the next scheduled run builds the article
                around it. Give it a date for things like the company
                anniversary — it'll be held until that day.
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => openSuggestionDialog(null)}
                sx={{ flexShrink: 0, ml: 2 }}
              >
                Add idea
              </Button>
            </Stack>

            {suggestions.length === 0 ? (
              <Box sx={{ p: 5, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No suggestions yet. Without one, the next run picks its own
                  topic from recent activity.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Idea</TableCell>
                      <TableCell>For</TableCell>
                      <TableCell>Use on</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestions.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell sx={{ maxWidth: 480 }}>
                          <Typography variant="body2">{s.idea}</Typography>
                          {s.notes && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {s.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{PLATFORM_LABEL[s.platform]}</TableCell>
                        <TableCell>
                          {s.use_on ? formatDate(s.use_on) : "Any run"}
                        </TableCell>
                        <TableCell>{s.priority}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.status}
                            color={STATUS_COLOR[s.status] || "default"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openSuggestionDialog(s)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSuggestion(s)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>This run costs money</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Generating writes two full articles — one for Medium, one for
            Hashnode — using Anthropic API credits.
          </Typography>
          <Alert severity="warning" icon={false} sx={{ mb: 1.5 }}>
            <strong>Roughly $0.55 (about ₹50) per run.</strong> It is billed
            whether or not you end up posting the articles.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            It takes a couple of minutes. The drafts appear here on their own
            and the email goes out when it's done — you don't need to wait on
            this screen.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Scheduled runs happen automatically. Only use this if you need an
            article outside that schedule.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleGenerate}>
            Yes, spend the credits
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing.id ? "Edit idea" : "Idea for the next blog"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="What should the article be about?"
              multiline
              minRows={3}
              fullWidth
              value={editing.idea}
              onChange={(e) =>
                setEditing({ ...editing, idea: e.target.value })
              }
              placeholder="e.g. How we wired the Ingram Micro reseller API into automated MS365 provisioning — and the VRI trap that cost us a week"
            />
            <TextField
              label="Anything else it should include (optional)"
              multiline
              minRows={2}
              fullWidth
              value={editing.notes}
              onChange={(e) =>
                setEditing({ ...editing, notes: e.target.value })
              }
              placeholder="Angle, numbers to mention, who it's for, a point you want made"
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Platform</InputLabel>
                <Select
                  label="Platform"
                  value={editing.platform}
                  onChange={(e) =>
                    setEditing({ ...editing, platform: e.target.value })
                  }
                >
                  <MenuItem value="both">Either platform</MenuItem>
                  <MenuItem value="medium">Medium only</MenuItem>
                  <MenuItem value="hashnode">Hashnode only</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Use on"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editing.use_on}
                onChange={(e) =>
                  setEditing({ ...editing, use_on: e.target.value })
                }
                helperText="Leave blank for the next run"
              />
              <TextField
                label="Priority"
                type="number"
                size="small"
                sx={{ width: 120 }}
                value={editing.priority}
                onChange={(e) =>
                  setEditing({ ...editing, priority: e.target.value })
                }
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSuggestion}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
